import { type BreadcrumbListSchema } from '@cx-web/api-clients/template-api/types';
import { escapeJsonForInlineScript, getRepubMetaFromRepub, removeRepubPrefix } from '@cx-web/utils';
import ChevronsRight from '~/vendor/cxy/icons/glyphs/legacy/ChevronsRight';
import HomeIcon from '~/vendor/cxy/icons/glyphs/legacy/HomeIcon';
import classNames from 'classnames';

import { type Repub } from '@cx-modules/constants';

import styles from './Breadcrumbs.module.scss';

export interface BreadcrumbsProps {
  dataSchema?: BreadcrumbListSchema;
  isUkFooterStyle?: boolean;
  path: string;
  repub: Repub;
}

export const getBreadcrumbSegments = (path: string, repub: Repub): string[] =>
  removeRepubPrefix(path, repub).split('/').filter(Boolean);

type Crumb = { href: string; name: string };

const toPathname = (id: string): string => new URL(id, 'http://localhost').pathname;

const crumbsFromSchema = ({ itemListElement }: BreadcrumbListSchema, homeHref: string): Crumb[] =>
  itemListElement
    .map(({ item }) => ({ href: toPathname(item['@id']), name: item.name }))
    .filter(({ href }) => href !== homeHref);

const crumbsFromPath = (segments: string[], urlRepub: string): Crumb[] =>
  segments.map((segment, idx) => ({
    href: `${urlRepub}/${segments.slice(0, idx + 1).join('/')}`,
    name: segment.replace(/-/g, ' '),
  }));

const normalizeName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

// The trail stays path-derived; a crumb takes cx-api's link only when cx-api has a crumb of the same name.
const withSchemaLinks = (pathCrumbs: Crumb[], schemaCrumbs: Crumb[]): Crumb[] =>
  pathCrumbs.map(crumb => {
    const match = schemaCrumbs.find(({ name }) => normalizeName(name) === normalizeName(crumb.name));
    return match ? { ...crumb, href: match.href } : crumb;
  });

export const footerRendersBreadcrumbs = ({
  isUkFooterStyle,
  path,
  repub,
}: {
  isUkFooterStyle?: boolean;
  path?: string;
  repub?: Repub;
}): boolean => isUkFooterStyle === true && !!path && !!repub;

export const Breadcrumbs = ({ dataSchema, isUkFooterStyle, path, repub }: BreadcrumbsProps): React.JSX.Element => {
  const { repubPath: urlRepub } = getRepubMetaFromRepub(repub);
  const segments = getBreadcrumbSegments(path, repub);

  const homeHref = `${urlRepub}/`;
  const pathCrumbs = crumbsFromPath(segments, urlRepub);
  const schemaCrumbs = dataSchema ? crumbsFromSchema(dataSchema, homeHref) : [];
  const crumbs = withSchemaLinks(pathCrumbs, schemaCrumbs);

  const jsonLd: BreadcrumbListSchema = dataSchema ?? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: pathCrumbs.map(({ href, name }, idx) => ({
      '@type': 'ListItem',
      item: { '@id': href, '@type': 'WebPage', name },
      position: idx + 1,
    })),
  };

  return (
    <nav aria-label="breadcrumbs" className={styles.breadcrumbsContainer} data-testid="breadcrumbs">
      <div className={classNames(styles.breadcrumbsWrapper, isUkFooterStyle && styles.breadcrumbsWrapperUk)}>
        <ol className={classNames(styles.breadcrumbsOl, isUkFooterStyle && styles.breadcrumbsOlUk)}>
          <li className={styles.breadCrumbLi}>
            <a data-testid="home-anchor" href={homeHref}>
              {isUkFooterStyle ? (
                <HomeIcon aria-label="home" className={styles.homeIcon} data-testid="home-image-uk" role="img" />
              ) : (
                <svg
                  aria-label="home"
                  data-testid="home-image"
                  fill="none"
                  height="14"
                  role="img"
                  viewBox="0 0 16 16"
                  width="14"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="m15.568 6.96-.001-.002L9.04.432A1.463 1.463 0 0 0 8 0c-.394 0-.764.153-1.042.432L.434 6.955l-.007.006a1.475 1.475 0 0 0 1.026 2.512h.26v4.803c0 .95.773 1.724 1.724 1.724H5.99c.26 0 .47-.21.47-.469v-3.765c0-.434.352-.787.786-.787h1.506c.434 0 .786.353.786.787v3.765c0 .26.21.469.47.469h2.553c.95 0 1.724-.773 1.724-1.724V9.473h.24c.394 0 .764-.153 1.042-.431a1.475 1.475 0 0 0 .001-2.083z"
                    fill="#D6D6D6"
                  />
                </svg>
              )}
            </a>
          </li>
          {crumbs.map(({ href, name }, idx) => {
            const isLast = idx === crumbs.length - 1;

            const text = (
              <span className={styles.breadCrumbText} data-testid="breadcrumb-text">
                {name}
              </span>
            );

            return (
              <li className={styles.breadCrumbLi} key={`${idx}-${href}`}>
                <span className={styles.arrowWrap}>
                  {isUkFooterStyle ? (
                    <ChevronsRight aria-hidden className={styles.arrowUk} data-testid="arrow-image-uk" />
                  ) : (
                    <svg
                      aria-label="arrow"
                      className={styles.arrow}
                      data-testid="arrow-image"
                      fill="none"
                      height="14"
                      role="img"
                      viewBox="0 0 9 16"
                      width="8"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M8 2 2 8l6 6" stroke="#fff" strokeWidth="2.304" />
                    </svg>
                  )}
                </span>
                {isLast ? (
                  <span className={styles.breadCrumbLast} data-testid="breadcrumb-last">
                    {text}
                  </span>
                ) : (
                  <a className={styles.breadCrumbLink} data-testid="breadcrumb-link" href={href} title={name}>
                    {text}
                  </a>
                )}
              </li>
            );
          })}
        </ol>
      </div>
      {(dataSchema || segments.length > 0) && (
        <script dangerouslySetInnerHTML={{ __html: escapeJsonForInlineScript(jsonLd) }} type="application/ld+json" />
      )}
    </nav>
  );
};
